/**
 * SyncStatusBadge — Editor ↔ Generator sync indicator (File 45).
 *
 * Shows the current sync state for a given editor with three states:
 *   GREEN  (IN_SYNC)        — editor and deliverable match
 *   YELLOW (EDITOR_AHEAD)   — editor has unsaved changes; "Apply Changes"
 *   RED    (CONFLICT)       — both sides changed; offer keep/keep/merge
 *   BLUE   (GENERATOR_ONLY) — editor empty, deliverable exists; "Load from Simulation"
 *
 * Polls /api/projects/{id}/sync/status/{editor_name} every 6s.
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "../../../lib/api";
import { CheckCircle2, AlertTriangle, AlertOctagon, Download, Save, RefreshCcw } from "lucide-react";

const COLOR_FOR = {
  IN_SYNC:        { bg: "#0B3B2E", border: "#10B981", fg: "#6EE7B7", icon: CheckCircle2 },
  EDITOR_AHEAD:   { bg: "#3F2A0F", border: "#F59E0B", fg: "#FCD34D", icon: AlertTriangle },
  CONFLICT:       { bg: "#3F0F0F", border: "#EF4444", fg: "#FCA5A5", icon: AlertOctagon },
  GENERATOR_ONLY: { bg: "#0F2A3F", border: "#3B82F6", fg: "#93C5FD", icon: Download },
  EDITOR_ONLY:    { bg: "#3F1D3F", border: "#A855F7", fg: "#D8B4FE", icon: AlertTriangle },
  EMPTY:          { bg: "#1F2937", border: "#6B7280", fg: "#9CA3AF", icon: AlertTriangle },
};

const LABEL_FOR = {
  IN_SYNC:        "In Sync",
  EDITOR_AHEAD:   "Editor Ahead",
  CONFLICT:       "Conflict",
  GENERATOR_ONLY: "Load From Sim",
  EDITOR_ONLY:    "No Generator",
  EMPTY:          "No Data",
};

export default function SyncStatusBadge({ projectId, editorName }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!projectId || !editorName) return;
    try {
      const r = await api.get(
        `/projects/${projectId}/sync/status/${editorName}`,
      );
      setStatus(r.data);
    } catch {
      // swallow — keep prior status
    }
  }, [projectId, editorName]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 6000);
    return () => clearInterval(t);
  }, [refresh]);

  if (!status || !status.status) {
    return (
      <span data-testid={`sync-badge-${editorName}-loading`}
            style={{ fontSize: 11, color: "#6B7280" }}>
        sync…
      </span>
    );
  }

  const info = COLOR_FOR[status.status] || COLOR_FOR.EMPTY;
  const Icon = info.icon;

  const onApply = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/projects/${projectId}/sync/harvest/${editorName}`,
      );
      if (r.data?.status === "SYNCED") {
        const stale = r.data.downstream_marked_stale || [];
        alert(
          `Changes applied. New deliverable revision created.\n\n` +
          (stale.length
            ? `${stale.length} downstream deliverable(s) marked stale:\n` +
              stale.slice(0, 10).join(", ") +
              (stale.length > 10 ? "..." : "")
            : "No downstream impact."),
        );
      } else {
        alert(r.data?.message || "Sync ok");
      }
      await refresh();
    } catch (e) {
      alert(`Apply failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onLoad = async () => {
    setBusy(true);
    try {
      await api.post(
        `/projects/${projectId}/sync/load-from-simulation/${editorName}`,
      );
      await refresh();
      window.location.reload(); // simplest way to refresh editor canvas
    } catch (e) {
      alert(`Load failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onResolve = async (resolution) => {
    setBusy(true);
    try {
      await api.post(
        `/projects/${projectId}/sync/resolve/${editorName}`,
        { resolution },
      );
      await refresh();
      if (resolution === "keep_generator") window.location.reload();
    } catch (e) {
      alert(`Resolve failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`sync-badge-${editorName}`}
         style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <div
        title={status.message}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 10px",
          background: info.bg,
          border: `1px solid ${info.border}`,
          borderRadius: 999,
          fontSize: 11, fontWeight: 600,
          color: info.fg,
          cursor: "help",
        }}
      >
        <Icon size={12} />
        <span>{LABEL_FOR[status.status]}</span>
        {status.stale_downstream > 0 && (
          <span style={{
            background: "#F59E0B", color: "#0B1220",
            borderRadius: 999, padding: "1px 6px", fontSize: 10,
            fontWeight: 700,
          }}>
            {status.stale_downstream}↓
          </span>
        )}
      </div>

      {status.status === "EDITOR_AHEAD" && (
        <button
          data-testid={`sync-apply-${editorName}-btn`}
          onClick={onApply}
          disabled={busy}
          title="Apply editor changes to deliverable (creates new revision)"
          style={{
            background: "#7C2D12", color: "#FED7AA",
            border: "1px solid #F97316",
            padding: "4px 10px", borderRadius: 6, fontSize: 11,
            fontWeight: 600, cursor: busy ? "wait" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 4,
          }}
        >
          <Save size={11} /> {busy ? "Applying…" : "Apply Changes"}
        </button>
      )}

      {status.status === "GENERATOR_ONLY" && (
        <button
          data-testid={`sync-load-${editorName}-btn`}
          onClick={onLoad}
          disabled={busy}
          title="Pull latest generator output into editor"
          style={{
            background: "#1E3A8A", color: "#BFDBFE",
            border: "1px solid #3B82F6",
            padding: "4px 10px", borderRadius: 6, fontSize: 11,
            fontWeight: 600, cursor: busy ? "wait" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 4,
          }}
        >
          <Download size={11} /> {busy ? "Loading…" : "Load from Simulation"}
        </button>
      )}

      {status.status === "CONFLICT" && (
        <>
          <button
            data-testid={`sync-keep-editor-${editorName}-btn`}
            onClick={() => onResolve("keep_editor")}
            disabled={busy}
            title="Discard generator changes, keep editor version"
            style={{
              background: "#7C2D12", color: "#FED7AA",
              border: "1px solid #F97316",
              padding: "4px 8px", borderRadius: 6, fontSize: 11,
              fontWeight: 600, cursor: busy ? "wait" : "pointer",
            }}
          >
            Keep Editor
          </button>
          <button
            data-testid={`sync-keep-generator-${editorName}-btn`}
            onClick={() => onResolve("keep_generator")}
            disabled={busy}
            title="Discard editor changes, reload from generator"
            style={{
              background: "#1E3A8A", color: "#BFDBFE",
              border: "1px solid #3B82F6",
              padding: "4px 8px", borderRadius: 6, fontSize: 11,
              fontWeight: 600, cursor: busy ? "wait" : "pointer",
            }}
          >
            Keep Generator
          </button>
        </>
      )}

      <button
        data-testid={`sync-refresh-${editorName}-btn`}
        onClick={refresh}
        title="Refresh sync status"
        style={{
          background: "transparent", border: "1px solid #374151",
          color: "#9CA3AF", padding: "3px 6px", borderRadius: 6,
          fontSize: 10, cursor: "pointer",
          display: "inline-flex", alignItems: "center",
        }}
      >
        <RefreshCcw size={10} />
      </button>
    </div>
  );
}
